import type {
  ActionTypeEnum,
  ApiNotificationConfiguration,
  ApiSettings,
  BranchDto,
  DatasourceEnvironments,
  HttpMethod,
  IntegrationDto,
  PlaceholdersInfo,
  ScheduleState,
  ActionType,
} from "@superblocksteam/shared";

import type {
  EventType,
  ExecuteActionCallStack,
} from "../../../legacy/constants/ActionConstants";
import type { RepoConnection } from "../../../legacy/constants/ReduxActionConstants";
import type { Api, ApiV2ExecutionResponse, Resolutions } from "./backend-types";

enum PaginationTypeDto {
  NONE,
  PAGE_NO,
  URL,
}

export type PropertyDto = {
  key?: string;
  value?: string;
  editable?: boolean;
  internal?: boolean;
  description?: string;
  mandatory?: boolean;
  type?: string;
  defaultValue?: string;
  minRange?: string;
  maxRange?: string;
  valueOptions?: string[];
};

export type ActionConfigurationDto = {
  httpMethod?: HttpMethod;
  path?: string;
  headers?: PropertyDto[];
  params?: PropertyDto[];
  body?: string;
  bodyFormData?: PropertyDto[];
  routeParams?: PropertyDto[];
  timeoutInMillisecond?: number;
  encodeParams?: boolean;
  paginationType?: PaginationTypeDto;
  next?: string;
  prev?: string;
  pluginSpecifiedTemplates?: PropertyDto[];
  urlBase?: string;
  urlPath?: string;
  filterBy?: string[];
  superblocksMetadata?: { pluginVersion?: string };
  openApiAction?: string;
  verboseHttpOutput?: boolean;
  doNotFailOnRequestError?: boolean;
  failOnGraphqlErrors?: boolean;
};

export type ActionDto = {
  id: string;
  name: string;
  type: ActionType;
  configuration: ActionConfigurationDto;
  applicationId?: string;
  organizationId?: string;
  datasourceId?: string;
  pluginId?: string;
  settings?: {
    executeOnLoad?: boolean;
    cacheResponse?: string;
    userSetOnLoad?: boolean;
    confirmBeforeExecute?: boolean;
  };
  children?: Record<string, string>;
};

export type ApiDto = {
  id: string;
  name?: string;
  apiPb?: Api;
  environment?: DatasourceEnvironments;
  deployedCommitId?: string;
  applicationId?: string;
  organizationId?: string;
  updated?: Date;
  scheduleState?: ScheduleState;
  folderId?: string | null;
  isDeployed?: boolean;
  isEditable?: boolean;
  canDelete?: boolean;
  sendEmailOnFailure?: boolean;
  integrations?: Record<string, IntegrationDto>;
  creator?: { id: string; name: string };
  settings?: ApiSettings;
  triggerType?: ApiTriggerType;
  repoConnection?: RepoConnection;
  currentBranch?: BranchDto;
  permissions?: ActionTypeEnum[];
  restricted?: boolean;
  lastDeployedAt?: Date;
};

export type ExecutionParamDto = { key: string; value: unknown };

export type ExecutionOutputDto = {
  error?: {
    fullErrorMessage: string;
    message: string;
    formPath: string;
    handled?: boolean;
  };
  log: string[];
  output: unknown;
  executionTime: number;
  request: string;
  placeholdersInfo?: PlaceholdersInfo;
};

export type BlockExecutionContext = Record<
  string,
  { value: string | number; label: string }
>;

export type SharedExecutionOutput = ExecutionOutputDto & {
  executionContext?: BlockExecutionContext;
  blockName?: string;
  endTimestamp?: number;
  fields?: Resolutions;
  errorLog?: string[];
};

export type OutputsDto = Record<string, ExecutionOutputDto>;

type ExecutionContextDto = {
  globals: Record<string, unknown>;
  outputs: OutputsDto;
  errorContext?: { actionId: string; actionName: string };
  error?: string;
};

export enum ApiTriggerType {
  UI,
  WORKFLOW,
  SCHEDULE,
}

interface ApiExecutionRequestDto {
  apiId: string;
  apiName?: string;
  params: ExecutionParamDto[];
  viewMode: boolean;
}

export interface ApiExecutionSagaPayload extends ApiExecutionRequestDto {
  environment: string;
  eventType: EventType;
  notifyOnSystemError: boolean;
  manualRun: boolean;
  callStack: ExecuteActionCallStack;
  spanId?: string;
  commitId?: string;
  formatCode?: boolean;
  callId?: string;
  inputs?: Record<string, unknown>;
  isTestRun?: boolean;
}

export type ApiExecutionResponseDto = {
  statusCode: string;
  context: ExecutionContextDto;
  systemError: string;
  apiId?: string;
  apiName?: string;
  notificationConfig?: ApiNotificationConfiguration;
  executionError?: string;
};

export type ExecutionResponse =
  | ApiV2ExecutionResponse
  | {
      systemError: string;
    };

export type BlockExecutionInfo = {
  executions: SharedExecutionOutput[];
  numFailed: number;
  numHandled: number;
  numPassed: number;
  numStarted: number;
};

export type EnrichedExecutionResult = {
  apiResult?: SharedExecutionOutput;
  lastOutputs?: Record<string, SharedExecutionOutput>;
  blockInfos?: Record<string, BlockExecutionInfo>;
  orderedExecutions?: SharedExecutionOutput[];
  systemError?: string;
  executionError?: string;
};

export type EnrichedExecutionProcessingOptions = {
  mergeExecutionEvents: boolean;
  blocksToReinitialize: string[];
  sendErrorOnUnknownBlockErrors?: boolean;
};

export type ApiDtoWithPb = {
  id: string;
  name?: string;
  triggerType?: ApiTriggerType;
  apiPb: Api;
  environment?: DatasourceEnvironments;
  deployedCommitId?: string;
  applicationId?: string;
  organizationId?: string;
  updated?: Date;
  scheduleState?: ScheduleState;
  folderId?: string | null;
  isDeployed?: boolean;
  isEditable?: boolean;
  canDelete?: boolean;
  sendEmailOnFailure?: boolean;
  integrations?: Record<string, IntegrationDto>;
  creator?: { id: string; name: string };
  settings?: ApiSettings;
  repoConnection?: RepoConnection;
  currentBranch?: BranchDto;
  permissions?: ActionTypeEnum[];
  restricted?: boolean;
  lastDeployedAt?: Date;
};

export function isApiDtoWithPb(value: unknown): value is ApiDtoWithPb {
  if (!value || typeof value !== "object" || !("apiPb" in value)) {
    return false;
  }
  const apiPb = value.apiPb;
  if (!apiPb || typeof apiPb !== "object" || !("metadata" in apiPb)) {
    return false;
  }
  const metadata = apiPb.metadata;
  return Boolean(
    "id" in value &&
    typeof value.id === "string" &&
    metadata &&
    typeof metadata === "object" &&
    "id" in metadata &&
    typeof metadata.id === "string" &&
    "name" in metadata &&
    typeof metadata.name === "string" &&
    "organization" in metadata &&
    typeof metadata.organization === "string" &&
    "trigger" in apiPb &&
    apiPb.trigger &&
    typeof apiPb.trigger === "object",
  );
}
